/**
 * Session feedback request schedule — WHEN a member is asked to rate a
 * session (session-feedback-cadence, 2026-10-21).
 *
 * Mario (21 Oct 2026): every member is asked by default, "after the first
 * session, and then every 5 sessions" — which he confirmed means sessions
 * 1, 6, 11, 16 … — and the two numbers are set on `/config` → Marketing
 * flows. A studio stores them in the optional `session_feedback_request`
 * block of its L2 config (`l2-yaml.ts`, a live `studio_config` field, so a
 * Save applies to the next completed session). This module holds the
 * defaults, the rule and its wording, so the saga that sends the ask
 * (`member_session_feedback_request`), the dashboard server that describes
 * it on `/flows`, and the `/config` editor that previews it agree by
 * construction.
 *
 * THE ORDINAL. A booking's place among the member's DELIVERED sessions —
 * completed, and not marked no-show or cancelled at attendance (the
 * `deliveredSessionSql` predicate) — counted in Clavis, oldest first. Bookings
 * from the system before the 19–20 Aug 2026 cutover were never imported, so a
 * long-standing client's first Clavis session is their session 1 here.
 *
 * Dependency-free leaf: imported by the SPA, the dashboard server and the
 * saga runtime alike, so it must stay free of Node and package imports.
 */

/** A studio's resolved schedule — both fields always present. */
export interface SessionFeedbackCadence {
  /** Ask after the member's Nth delivered session (1 or more). */
  readonly first_after_session: number;
  /** Then ask again every M sessions after that; 0 = only once. */
  readonly then_every_sessions: number;
}

/** Mario's schedule, and every studio's until it sets its own. */
export const SESSION_FEEDBACK_CADENCE_DEFAULTS: SessionFeedbackCadence = {
  first_after_session: 1,
  then_every_sessions: 5,
};

/** The largest value either field takes — a sanity cap, not a business rule. */
export const SESSION_FEEDBACK_CADENCE_MAX = 100;

/**
 * Fill a stored (possibly partial or absent) block with the defaults, field
 * by field. The block is validated where it is parsed (`l2-yaml.ts`); this
 * only resolves what is missing.
 */
export function resolveSessionFeedbackCadence(
  block:
    | {
        readonly first_after_session?: number | undefined;
        readonly then_every_sessions?: number | undefined;
      }
    | undefined,
): SessionFeedbackCadence {
  return {
    first_after_session:
      block?.first_after_session ??
      SESSION_FEEDBACK_CADENCE_DEFAULTS.first_after_session,
    then_every_sessions:
      block?.then_every_sessions ??
      SESSION_FEEDBACK_CADENCE_DEFAULTS.then_every_sessions,
  };
}

/**
 * Is the member's `ordinal`-th delivered session one the schedule asks after?
 * Yes at `first_after_session`, then every `then_every_sessions` sessions after
 * it (never, when that is 0). An ordinal that is not a positive integer is
 * never due.
 */
export function isSessionFeedbackDue(
  ordinal: number,
  cadence: SessionFeedbackCadence,
): boolean {
  if (!Number.isInteger(ordinal) || ordinal < 1) return false;
  const first = cadence.first_after_session;
  const every = cadence.then_every_sessions;
  if (ordinal === first) return true;
  return every > 0 && ordinal > first && (ordinal - first) % every === 0;
}

/** The first `count` sessions the schedule asks after, in order. */
export function listSessionFeedbackDueSessions(
  cadence: SessionFeedbackCadence,
  count: number,
): ReadonlyArray<number> {
  if (cadence.then_every_sessions === 0) return [cadence.first_after_session];
  const out: number[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push(cadence.first_after_session + i * cadence.then_every_sessions);
  }
  return out;
}

/**
 * The schedule in plain words, lower-case so a caller can lead with its own
 * subject: "after session 1, then every 5 sessions (sessions 1, 6, 11, 16 …)",
 * "after session 3, then after every session (sessions 3, 4, 5, 6 …)" or
 * "after session 1 only".
 */
export function describeSessionFeedbackCadence(
  cadence: SessionFeedbackCadence,
): string {
  const first = `after session ${cadence.first_after_session}`;
  const every = cadence.then_every_sessions;
  if (every === 0) return `${first} only`;
  const then = every === 1 ? "then after every session" : `then every ${every} sessions`;
  const list = listSessionFeedbackDueSessions(cadence, 4).join(", ");
  return `${first}, ${then} (sessions ${list} …)`;
}
